import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { Monitor, Download, AlertCircle, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { useMachine } from '../api/hooks/updates/useMachineQuery';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import {
  Button,
  Badge,
  Card,
  PageHeader,
} from '@license-management/ui';

const MachineDebug: React.FC = () => {
  const { machineId } = useParams<{ machineId: string }>();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<'overview' | 'events' | 'downloads' | 'activations'>('overview');
  
  const { data: machineInfo, isLoading, error } = useMachine(machineId || '');

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !machineInfo || !machineInfo.machine) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Machine Debug"
          description="Machine not found"
          actions={
            <Button
              variant="secondary"
              onClick={() => navigate('/releases')}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Back to Releases
            </Button>
          }
        />
        <ErrorMessage message={error?.message || 'Machine not found'} />
      </div>
    );
  }

  const { machine, activations, updateHistory, downloadHistory } = machineInfo;

  const getEventIcon = (eventType: string, success: boolean) => {
    if (!success) return <XCircle className="h-5 w-5 text-red-500" />;
    
    switch (eventType) {
      case 'check':
        return <Monitor className="h-5 w-5 text-blue-500" />;
      case 'download_start':
      case 'download_complete':
        return <Download className="h-5 w-5 text-green-500" />;
      case 'download_error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'install_complete':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'install_error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-zinc-500" />;
    }
  };

  const getEventDescription = (event: any) => {
    switch (event.eventType) {
      case 'check':
        return `Checked for updates (current: ${event.fromVersion})`;
      case 'download_start':
        return `Started downloading ${event.toVersion}`;
      case 'download_complete':
        return `Completed download of ${event.toVersion}`;
      case 'download_error':
        return `Download failed: ${event.errorMessage || event.errorCode}`;
      case 'install_start':
        return `Started installing ${event.toVersion}`;
      case 'install_complete':
        return `Successfully installed ${event.toVersion}`;
      case 'install_error':
        return `Installation failed: ${event.errorMessage || event.errorCode}`;
      case 'rollback':
        return `Rolled back from ${event.fromVersion} to ${event.toVersion}`;
      default:
        return event.eventType;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Machine Debug: ${machine.displayId}`}
        description={`Machine ID Hash: ${machine.machineIdHash.substring(0, 16)}...`}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate('/releases')}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back to Releases
          </Button>
        }
      />

      {/* Machine Overview */}
      <Card className="p-6">
        <h2 className="text-lg font-medium text-zinc-900 mb-4">Machine Information</h2>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-zinc-500">Platform</dt>
            <dd className="mt-1 text-sm text-zinc-900">{machine.platform} ({machine.architecture})</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-zinc-500">Current Version</dt>
            <dd className="mt-1 text-sm text-zinc-900">{machine.lastVersion || 'Unknown'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-zinc-500">OS Version</dt>
            <dd className="mt-1 text-sm text-zinc-900">{machine.osVersion || 'Unknown'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-zinc-500">App Version</dt>
            <dd className="mt-1 text-sm text-zinc-900">{machine.appVersion || 'Unknown'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-zinc-500">First Seen</dt>
            <dd className="mt-1 text-sm text-zinc-900">
              {format(new Date(machine.firstSeen), 'PPP')}
              <span className="text-zinc-500 ml-1">
                ({formatDistanceToNow(new Date(machine.firstSeen), { addSuffix: true })})
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-zinc-500">Last Seen</dt>
            <dd className="mt-1 text-sm text-zinc-900">
              {format(new Date(machine.lastSeen), 'PPP')}
              <span className="text-zinc-500 ml-1">
                ({formatDistanceToNow(new Date(machine.lastSeen), { addSuffix: true })})
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-zinc-500">Total Update Checks</dt>
            <dd className="mt-1 text-sm text-zinc-900">{machine.totalUpdateChecks}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-zinc-500">Total Downloads</dt>
            <dd className="mt-1 text-sm text-zinc-900">{machine.totalDownloads}</dd>
          </div>
        </dl>
        {machine.notes && (
          <div className="mt-6">
            <dt className="text-sm font-medium text-zinc-500">Notes</dt>
            <dd className="mt-1 text-sm text-zinc-900">{machine.notes}</dd>
          </div>
        )}
      </Card>

      {/* Tabs */}
      <div className="border-b border-zinc-200">
        <nav className="flex space-x-4" aria-label="Tabs">
          <button
            onClick={() => setSelectedTab('events')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              selectedTab === 'events'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'
            }`}
          >
            Update Events ({updateHistory.length})
          </button>
          <button
            onClick={() => setSelectedTab('downloads')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              selectedTab === 'downloads'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'
            }`}
          >
            Downloads ({downloadHistory.length})
          </button>
          <button
            onClick={() => setSelectedTab('activations')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              selectedTab === 'activations'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'
            }`}
          >
            License Activations ({activations.length})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <Card className="overflow-hidden">
        {selectedTab === 'events' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-zinc-900 mb-4">Update Event History</h3>
            <div className="space-y-4">
              {updateHistory.map((event) => (
                <div key={event.id} className="flex items-start space-x-3">
                  {getEventIcon(event.eventType, event.success)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-900">{getEventDescription(event)}</p>
                    <p className="text-xs text-zinc-500">
                      {format(new Date(event.timestamp), 'PPp')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTab === 'downloads' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-zinc-200">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Started
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Completed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Bytes Downloaded
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-zinc-200">
                {downloadHistory.map((download) => (
                  <tr key={download.id} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {format(new Date(download.startedAt), 'PPp')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {download.completedAt ? format(new Date(download.completedAt), 'PPp') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {download.bytesDownloaded ? `${(download.bytesDownloaded / 1024 / 1024).toFixed(2)} MB` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {download.errorCode ? (
                        <Badge variant="error">Failed: {download.errorCode}</Badge>
                      ) : download.completedAt ? (
                        <Badge variant="success">Success</Badge>
                      ) : (
                        <Badge variant="warning">In Progress</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedTab === 'activations' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-zinc-200">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    License Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-zinc-200">
                {activations.map((activation) => (
                  <tr key={activation.activationId} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-zinc-900">
                      {activation.licenseKey}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Badge variant={activation.status === 'active' ? 'success' : 'error'}>
                        {activation.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-500">
                      {format(new Date(activation.createdAt), 'PPP')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default MachineDebug;